// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadAccountsReceivable from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/LoadAccountsReceivable";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    accountsReceivable: getCurrentBillToState(state).value?.accountsReceivable,
    displayAccountsReceivableBalances: getSettingsCollection(state).customerSettings.displayAccountsReceivableBalances,
});

const mapDispatchToProps = {
    loadAccountsReceivable,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryBucketsStyles {
    container?: GridContainerProps;
    item?: GridItemProps;
    // XNG-Changes
    dataTableStyle?: DataTableProps;
    dataTableHeaderStyle?: DataTableHeaderPresentationProps;
    dataTableCellStyle?: DataTableCellProps;
}

export const bucketsStyles: InvoiceHistoryBucketsStyles = {
    container: {
        gap: 0,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        overflow: auto;
                    `,
                    css`
                        overflow: auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    item: {
        width: 12,
        style: {
            paddingBottom: "20px",
            minWidth: "600px",
        },
    },
};

const styles = bucketsStyles;

class InvoiceHistoryBuckets extends React.Component<Props> {
    componentDidMount() {
        if (!this.props.accountsReceivable && this.props.displayAccountsReceivableBalances) {
            this.props.loadAccountsReceivable();
        }
    }

    render() {
        const { accountsReceivable, displayAccountsReceivableBalances } = this.props;
        if (!accountsReceivable || !displayAccountsReceivableBalances) {
            return null;
        }

        // XNG-Changes:use table to match classic
        return (
            <>
                {/* <GridContainer {...styles.container} data-test-selector="invoiceHistory_invoiceBalance">
                    <GridItem {...styles.item}>
                        {accountsReceivable.agingBuckets!.map((agingBucket, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <SmallHeadingAndText
                                heading={agingBucket.label}
                                text={agingBucket.amountDisplay}
                                key={index}
                            />
                        ))}
                        <SmallHeadingAndText
                            heading={accountsReceivable.agingBucketTotal!.label}
                            text={accountsReceivable.agingBucketTotal!.amountDisplay}
                        />
                    </GridItem>
                </GridContainer> */}
                <DataTable {...styles.dataTableStyle}>
                    <DataTableHead>
                        {accountsReceivable.agingBuckets!.map((agingBucket, index) => (
                            <DataTableHeader key={index} tight {...styles.dataTableHeaderStyle}>
                                {agingBucket.label}
                            </DataTableHeader>
                        ))}
                        <DataTableHeader tight {...styles.dataTableHeaderStyle}>
                            {accountsReceivable.agingBucketTotal!.label}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        <DataTableRow>
                            {accountsReceivable.agingBuckets!.map((agingBucket, index) => (
                                <DataTableCell key={index} {...styles.dataTableCellStyle}>
                                    {agingBucket.amountDisplay}
                                </DataTableCell>
                            ))}
                            <DataTableCell {...styles.dataTableCellStyle}>
                                {accountsReceivable.agingBucketTotal!.amountDisplay}
                            </DataTableCell>
                        </DataTableRow>
                    </DataTableBody>
                </DataTable>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryBuckets),
    definition: {
        group: "Invoice History",
        displayName: "Aging Buckets",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
